import { useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import CategoryTabs from '@/components/base/CategoryTabs';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === slug),
    [slug],
  );

  const categoryArticles = useMemo(() => {
    if (!slug) return [];
    return articles.filter((a) => a.category === slug);
  }, [slug]);

  useEffect(() => {
    if (currentCategory) {
      document.title = `${currentCategory.name} - 黑料吃瓜网 | 免费高清完整视频在线播放`;
    } else {
      document.title = '分类未找到 - 黑料吃瓜网';
    }
  }, [currentCategory]);

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
          <h1 className="font-heading text-2xl text-foreground-950 mb-4">分类未找到</h1>
          <p className="text-foreground-600">该分类不存在或已被移除。</p>
        </main>
        <Footer />
      </div>
    );
  }

  const articleListSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 黑料吃瓜网`,
    description: currentCategory.description,
    url: `https://heiliaochigua.com/category/${currentCategory.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '黑料吃瓜网',
      url: 'https://heiliaochigua.com',
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">{JSON.stringify(articleListSchema)}</script>
      <Navbar />
      <main>
        <section className="bg-background-900 relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Dark%20mysterious%20abstract%20texture%20with%20warm%20subtle%20amber%20and%20deep%20red%20accents%20clean%20minimal%20gradient%20background%20editorial%20quality%20soft%20atmospheric%20mood&width=1600&height=300&seq=cat-header-bg&orientation=landscape"
              alt=""
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-background-900"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 py-12">
            <div className="mb-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: currentCategory.name },
                ]}
              />
            </div>
            <h1 className="font-heading text-3xl text-white mb-2">{currentCategory.name}</h1>
            <p className="text-white/70 text-sm">{currentCategory.description}</p>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <CategoryTabs categories={categories} activeSlug={slug || ''} />

          <div className="mt-8">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <ArticleCard
                    key={article.id}
                    slug={article.slug}
                    title={article.title}
                    excerpt={article.excerpt}
                    image={article.image}
                    date={article.date}
                    views={article.views}
                    tags={article.tags}
                    categoryName={currentCategory.name}
                    categorySlug={currentCategory.slug}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <i className="ri-inbox-line w-12 h-12 flex items-center justify-center text-foreground-300 mx-auto mb-4"></i>
                <p className="text-foreground-600">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}